import { useState, useEffect, useLayoutEffect, Fragment } from "react";
import { format, parse, isBefore, isAfter, isEqual, addHours, differenceInMilliseconds, differenceInMinutes } from "date-fns";  // Import necessary date-fns functions
import { CheckCircleIcon, ChevronDoubleRightIcon, ClipboardDocumentCheckIcon, ClockIcon, ExclamationCircleIcon, QuestionMarkCircleIcon, MapPinIcon, UserCircleIcon } from "@heroicons/react/20/solid";
import { useNavigate } from 'react-router-dom';
import { useContext } from "react";
import { AuthContext } from "../AuthContext";  // Import the AuthContext
import SubstepViewer from "./viewsubsteps";
import { ChatBubbleBottomCenterIcon } from "@heroicons/react/24/solid";
import React from "react"

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function DailyTaskTimeline() {
  const [tasks, setTasks] = useState([]);
  const [currentTask, setCurrentTask] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [timeToNextTask, setTimeToNextTask] = useState(0);  // Time to next task in milliseconds
  const [elapsedTime, setElapsedTime] = useState(0);  // Time elapsed since last update
  const [progress, setProgress] = useState(0);  // Progress bar value
  const [raiseIssue, setRaiseIssue] = useState(null);
  const [struggle, setStruggle] = useState("");
  const [resolution, setResolution] = useState("");
  const [ask, setAsk] = useState("manager");
  const [substeps, setSubsteps] = useState([]);
  const [substepIssue, setSubstepIssue] = useState("");
  const [callData, setCallData] = useState(null);
  const [instructionText, setInstructionText] = useState("");
  const [openOutcome, setOpenOutcome] = useState(null);
  const [openMessages, setOpenMessages] = useState(null);
  const [currentTaskMessages, setCurrentTaskMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [messagesTaskId, setMessagesTaskId] = useState(null);
  
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [ratings, setRatings] = useState({});
  const [todayMeetings, setTodayMeetings] = useState([]);

  const getTaskEndTime = (task, taskStartTime) => {
    const rawEnd = String(task?.EndTime || '').substring(0, 8);
    if (rawEnd) {
      const parsedEnd = parse(rawEnd, 'HH:mm:ss', new Date());
      if (!isNaN(parsedEnd) && isAfter(parsedEnd, taskStartTime)) {
        return parsedEnd;
      }
    }
    return addHours(taskStartTime, 1);
  };

  const user = useContext(AuthContext);  // Access the user state from AuthContext

  const handleRating = async (helpId, rating) => {
    try {
      await fetch(`https://api.insightos.co.uk/api/ratehelp/${helpId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: 'include',
        body: JSON.stringify({ Rating: rating }),
      });
      setRatings(prev => ({ ...prev, [helpId]: rating }));
      fetchTasks();
    } catch (error) {
      console.error("Error rating help:", error);
    }
  };

  const handleTaskSelect = (taskId) => {
    setSelectedTaskId(taskId === selectedTaskId ? null : taskId); // Toggle selection
  };

  // Handle opening messages for a task
  const handleOpenMessages = (taskId, messages) => {
    setMessagesTaskId(taskId);
    setCurrentTaskMessages(messages || []);
    setSelectedTaskId(null); // Close substep viewer if open
  };

  // Handle sending a new message
  const handleSendMessage = async () => {
    if (!messageInput.trim() || !messagesTaskId) return;

    try {
      const response = await fetch("https://api.insightos.co.uk/api/sendtaskmessage", {
        method: "POST",
        headers: { 
          'Content-Type': 'application/json' 
        }, 
        credentials: 'include',
        body: JSON.stringify({ 
          TaskID: messagesTaskId,
          content: messageInput,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to send message.");
      }

      const data = await response.json();

      if (data.success) {
        // Add the new message to the current messages
        const newMessage = {
          id: Date.now(), // Temporary ID
          content: messageInput,
          from: user.email,
          to: user.manager || user.email,
          createdAt: new Date().toISOString(),
          TaskId: messagesTaskId
        };
        
        setCurrentTaskMessages(prev => [...prev, newMessage]);
        setMessageInput(""); // Clear input
        
        // Refresh tasks to get updated message count
        fetchTasks();
      }
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Failed to send message. Please try again.");
    }
  };


  const Navigate = useNavigate();
  

  // Fetch tasks from the backend
    async function fetchTasks() {
      const token = localStorage.getItem('token');
      try {
        const response = await fetch("https://api.insightos.co.uk/api/mydailytasks", {
          method: "GET",
          headers: { 'Content-Type': 'application/json', }, credentials: 'include',
        });

        if (!response.ok) {
          throw new Error("Failed to fetch tasks.");
        }

        const data = await response.json();

        // Sort tasks by StartTime (chronological order)
        const sortedTasks = data.data.sort((a, b) =>
          parse(a.StartTime, "HH:mm:ss", new Date()) - parse(b.StartTime, "HH:mm:ss", new Date())
        );

        console.log("sortedTasks:", sortedTasks);

        setTasks(sortedTasks);

        // Determine the current task based on the current time
        const now = new Date();
        const current = sortedTasks.find((task) => {
          const taskStartTime = parse(task.StartTime, "HH:mm:ss", new Date());
          const taskEndTime = getTaskEndTime(task, taskStartTime);
          return isEqual(taskStartTime, now) || (isBefore(taskStartTime, now) && isAfter(taskEndTime, now));
        });

        if (current) {
          setCurrentTask(current);
        }

        // Find the next task and calculate time to it
        const nextTask = sortedTasks.find((task) => {
          const taskStartTime = parse(task.StartTime, "HH:mm:ss", new Date());
          return isAfter(taskStartTime, now);
        });

        if (nextTask) {
          const nextTaskStartTime = parse(nextTask.StartTime, "HH:mm:ss", new Date());
          const timeDiff = differenceInMilliseconds(nextTaskStartTime, now);
          setTimeToNextTask(timeDiff);
        }
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
    }

    useEffect(() => {

    fetchTasks();

    }, []);

    // Fetch today's meetings and filter by attendees for staff users
    const fetchTodayMeetings = async () => {
      try {
        const todayStr = format(new Date(), 'yyyy-MM-dd');
        const response = await fetch(`https://api.insightos.co.uk/api/meetings/${todayStr}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) return;
        const data = await response.json();
        const normalizeAttendees = (value) =>
          String(value || '')
            .split(',')
            .map((e) => e.trim().toLowerCase())
            .filter(Boolean);
        const matchesUser = (item) => {
          const access = String(user?.access || '').toLowerCase();
          if (access !== 'staff') return true;
          const name = String(user?.name || '').trim().toLowerCase();
          if (!name) return false;
          return normalizeAttendees(item?.Attendees || item?.MeetingAttendees || '').includes(name);
        };
        const rows = Array.isArray(data.data) ? data.data.filter(matchesUser) : [];
        setTodayMeetings(rows);
      } catch (err) {
        console.error('Error fetching today meetings:', err);
      }
    };

    useEffect(() => { fetchTodayMeetings(); }, []);

    const handleCompleteTask = async (taskId) => {

      setOpenOutcome(taskId);
  
    
    };



    useEffect(() => {
      const fetchSubsteps = async () => {
        try {
          const response = await fetch(`https://api.insightos.co.uk/api/getsubsteps/${callData}`, {
            method: "GET",  // ✅ Move method outside headers
            headers: {
              "Content-Type": "application/json",
            },
            credentials: 'include',  // ✅ Allows cookies to be sent with requests
          });
    
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          const substeps = await response.json();
          console.log("substeps:", substeps);
          setSubsteps(substeps.data);
        } catch (error) {
          console.error("Failed to fetch substeps:", error);
        }
      };
    
      fetchSubsteps();
    }, [callData]);
    


  // Update the current time every second
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(intervalId);  // Cleanup on unmount
  }, []);

  // Update progress bar every second
  useEffect(() => {
    if (timeToNextTask > 0) {
      const intervalId = setInterval(() => {
        setElapsedTime((prevTime) => prevTime + 1000);  // Increase elapsed time by 1 second

        // Calculate the progress as a percentage of the total time
        const progressPercent = (elapsedTime / timeToNextTask) * 100;

        if (progressPercent < 100) {
          setProgress(progressPercent);
        } else {
          setProgress(100);
        }
      }, 1000);

      return () => clearInterval(intervalId); // Cleanup on unmount
    }
  }, [timeToNextTask, elapsedTime]);

  // Scroll to current task after tasks have been rendered
  useLayoutEffect(() => {
    if (currentTask && tasks.length > 0) {
      const currentTaskElement = document.getElementById(currentTask.TaskID); // Ensure we're getting the correct element
      if (currentTaskElement) {
        currentTaskElement.scrollIntoView({ behavior: 'smooth', block: 'center' }); // Scroll smoothly to the current task
      }
    }
  }, [currentTask, tasks]); // Re-run if currentTask or tasks change

  // Handle task completion

  const handleSaveOutcome = async (taskId) => {

    const token = localStorage.getItem('token');
  
    try {
  
      const response = await fetch(`https://api.insightos.co.uk/api/updatedailytask/${taskId}`, {
  
        method: "PUT",
        headers: { 'Content-Type': 'application/json', }, credentials: 'include',
        body: JSON.stringify({ instructionText: instructionText }),
      });
  
      if (!response.ok) {
        throw new Error("Failed to update task.");
      }
  
      const data = await response.json();
  
      if (data.success) {
        console.log("Task updated successfully.");
        setOpenOutcome(null);
        setInstructionText("");

        fetchTasks();
      }
  
    } catch (error) {
  
      console.error("Error updating task:", error);
  
    }
  
  };

  // Handle issue submission

  const handleIssueSubmit = async (taskId) => {
    const token = localStorage.getItem('token');

    console.log("taskId:", taskId);

    console.log("substep:", substepIssue);

    console.log("struggle:", struggle);


    try {
      const response = await fetch(`https://api.insightos.co.uk/api/raiseissue`, {
        method: "POST",
        headers: { 'Content-Type': 'application/json', }, credentials: 'include',
        body: JSON.stringify({ TaskID: taskId, Struggle: struggle, Resolution: resolution, Ask: ask, Substep: substepIssue, manager:user.manager  }),
      });

      if (!response.ok) {
        throw new Error("Failed to raise issue.");
      }

      setRaiseIssue(null);  // Close the issue form
      setStruggle("");  // Reset the struggle description
      setResolution("");  // Reset the resolution description
    } catch (error) {
      console.error("Error raising issue:", error);
    }
  }

  // Handle issue raise
  const handleRaiseIssue = (taskId, id) => {
    setRaiseIssue(taskId);

setCallData(id);  }


  // Handle course viewer
 


  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-indigo-50/70 via-sky-50/60 to-emerald-50/60" data-component="task-calendar-today">
    {/* Header with date, current task, and current time - no longer sticky/fixed */}
    <header className="rounded-2xl border border-indigo-200/60 bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl shadow-[0_12px_30px_rgba(99,102,241,0.2)] p-4 md:p-6 mb-3 text-center md:text-left">
      <div className="flex flex-col items-center md:flex-row md:items-center md:justify-between md:gap-8 gap-3">
        <h1 className="text-lg md:text-xl font-bold text-indigo-700">
          {format(new Date(), "EEEE, MMMM d, yyyy")}
        </h1>
        <div className="flex flex-col items-center md:flex-row md:items-center md:gap-3 gap-2 w-full md:w-auto">
          <h3 className="text-xs md:text-base text-white px-3 py-2 rounded-xl bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border border-white/20 shadow-lg shadow-indigo-950/35">
            {user.name}
          </h3>
          <span className="text-xs md:text-sm text-indigo-900 rounded-xl border border-indigo-200/70 bg-white/75 backdrop-blur-sm px-3 py-2">
            Current Task:{" "}
            <span className="font-semibold text-indigo-700">
              {currentTask ? currentTask.Task : "None"}
            </span>
          </span>
          <div className="inline-flex items-center px-3 py-2 bg-gradient-to-br from-sky-600/90 to-sky-500/90 rounded-xl border border-white/20 shadow-lg shadow-sky-900/35 md:ml-auto">
            <ClockIcon className="h-5 w-5 text-white" aria-hidden="true" />
            <span className="text-lg text-white ml-1 font-bebas">
              {format(currentTime, "HH:mm")}
            </span>
          </div>
        </div>
      </div>
    </header>
    {tasks.length === 0 && (
      <div className="p-3 md:p-4 rounded-2xl border border-indigo-200/60 bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl shadow-[0_12px_30px_rgba(99,102,241,0.2)] text-indigo-900 flex flex-col md:flex-row items-center justify-between">
        <ExclamationCircleIcon className="h-8 w-8 md:h-12 md:w-12 mb-2 md:mb-0" aria-hidden="true" />
        <p className="text-base md:text-lg text-indigo-900 text-center md:text-left my-2 md:my-0 font-semibold">
          No tasks scheduled for today.
        </p>
        {user.access !== 'staff' && (
        <button onClick={() => Navigate('/taskcalendar')}
        data-feature="open-task-manager"
        type="button"
        className="inline-flex items-center gap-x-1.5 rounded-xl bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border border-white/20 px-2.5 py-1.5 text-sm font-semibold text-white shadow-lg shadow-indigo-950/35 hover:from-indigo-800/95 hover:to-indigo-600/95 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 mt-2 md:mt-0"
      >
        <ClipboardDocumentCheckIcon aria-hidden="true" className="-ml-0.5 size-4 md:size-5" />
        Task Manager 
      </button>
      )}
        
      </div>
      )}
      {raiseIssue && (
    <div className="fixed inset-0 flex items-center justify-center bg-indigo-950/40 backdrop-blur-sm z-50 p-3 overflow-y-auto">
    <div className="w-full max-w-lg bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl border border-indigo-200/60 p-4 md:p-8 rounded-2xl shadow-[0_12px_30px_rgba(99,102,241,0.25)] my-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">Raise an Issue</h2>
        <button 
          onClick={() => setRaiseIssue(null)}
          className="text-gray-500 hover:text-gray-700"
          data-feature="close-issue-modal"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Struggle */}
      <div className="mb-4 md:mb-6">
        <label htmlFor="struggle" className="block text-sm font-medium text-gray-700">
          What are you struggling with?
        </label>
        <textarea
          id="struggle"
          onChange={(e) => setStruggle(e.target.value)}
          name="struggle"
          value={struggle}
          rows={3}
          placeholder="Describe your issue..."
          className="mt-1 block w-full text-sm border border-indigo-200/70 bg-white/80 rounded-xl p-2 text-indigo-900 shadow-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
        />
      </div>

      {/* Resolution */}
      <div className="mb-4 md:mb-6">
        <label htmlFor="resolution" className="block text-sm font-medium text-gray-700">
          How have you tried to resolve the issue?
        </label>
        <textarea
          id="resolution"
          onChange={(e) => setResolution(e.target.value)}
          value={resolution}
          name="resolution"
          rows={3}
          placeholder="Explain what steps you've taken..."
          className="mt-1 block w-full text-sm border border-indigo-200/70 bg-white/80 rounded-xl p-2 text-indigo-900 shadow-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
        />
      </div>

      {/* Substeps */}
      <div className="mb-4 md:mb-6">
        <label htmlFor="substep" className="block text-sm font-medium text-gray-700">
          Substeps
        </label>
        <select
          onChange={(e) => setSubstepIssue(e.target.value)}
          value={substepIssue}
          id="substep"
          name="substep"
          className="mt-1 block w-full text-sm border border-indigo-200/70 bg-white/80 rounded-xl p-2 text-indigo-900 shadow-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
        >
          <option value="" disabled>Select a substep...</option>
          {substeps.map((substep) => (
            <option key={substep.id} value={substep.id}>
              {substep.Name}
            </option>
          ))}
        </select>
      </div>

      {/* Ask For Help */}
      <div className="mb-4 md:mb-6">
        <label htmlFor="ask" className="block text-sm font-medium text-gray-700">
          Who would you like to ask for help?
        </label>
        <select
          id="ask"
          onChange={(e) => setAsk(e.target.value)}
          value={ask}
          name="ask"
          className="mt-1 block w-full text-sm border border-indigo-200/70 bg-white/80 rounded-xl p-2 text-indigo-900 shadow-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
        >
          <option value="" disabled>Select an option...</option>
          <option value="manager">Manager</option>
          <option value="team">Team</option>
        </select>
      </div>

      {/* Submit Button */}
      <div className="flex justify-between mt-6">
        <button
          onClick={() => setRaiseIssue(null)}
          type="button"
          data-feature="cancel-issue-draft"
          className="rounded-xl bg-white/70 border border-indigo-200/70 px-4 py-2 text-sm font-medium text-indigo-900 shadow-sm hover:bg-white transition-all focus:outline-none"
        >
          Cancel
        </button>
        <button
          onClick={() => handleIssueSubmit(raiseIssue)}
          type="button"
          data-feature="submit-issue_post"
          data-component="submit-issue"
          className="rounded-xl bg-gradient-to-br from-sky-600/90 to-sky-500/90 border border-white/20 px-4 py-2 text-sm font-medium text-white shadow-lg shadow-sky-900/35 hover:from-sky-500/95 hover:to-sky-400/95 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-600"
        >
          Ask For Help
        </button>
      </div>
    </div>
  </div>
)}


  
  
      

      {/* Task List */}
      <div className="flex flex-col md:flex-row h-full">
      {/* Task List: Takes full width on mobile, 2/3 of the screen on larger devices if a task is selected */}
      <div className={`overflow-auto bg-gradient-to-br from-indigo-50/50 via-sky-50/40 to-emerald-50/50 p-3 md:p-4 pb-24 sm:pb-4 rounded-2xl border border-indigo-200/50 transition-all duration-300 
        ${selectedTaskId ? "w-full md:w-2/3" : "w-full"}`}>
        <ul className="space-y-3 md:space-y-4">
          {/* Build merged timeline: tasks + today's meetings, sorted by start time */}
          {(() => {
            // Parse meeting time helpers
            const parseMeetingTime = (timeStr) => {
              if (!timeStr) return null;
              const clean = String(timeStr).trim();
              // try HH:mm:ss then HH:mm
              try {
                const d = parse(clean, 'HH:mm:ss', new Date());
                if (!isNaN(d)) return d;
              } catch {}
              try {
                const d = parse(clean, 'HH:mm', new Date());
                if (!isNaN(d)) return d;
              } catch {}
              return null;
            };

            // For each task determine if it is fully eclipsed by a meeting
            const isTaskEclipsedByMeeting = (task) => {
              const tStart = parse(task.StartTime, 'HH:mm:ss', new Date());
              const tEnd = getTaskEndTime(task, tStart);
              return todayMeetings.some((m) => {
                const mStart = parseMeetingTime(m.Time);
                const mEnd = parseMeetingTime(m.FinishTime);
                if (!mStart || !mEnd) return false;
                const durationMins = differenceInMinutes(mEnd, mStart);
                // Meeting eclipses task if: meeting overlaps task AND (meeting > 1h OR meeting fully covers task)
                const overlaps = isBefore(mStart, tEnd) && isAfter(mEnd, tStart);
                if (!overlaps) return false;
                return durationMins >= 60 || (isBefore(mStart, tStart) || isEqual(mStart, tStart)) && (isAfter(mEnd, tEnd) || isEqual(mEnd, tEnd));
              });
            };

            // Build combined items list
            const taskItems = tasks
              .filter((t) => !isTaskEclipsedByMeeting(t))
              .map((t) => ({ type: 'task', sortTime: parse(t.StartTime, 'HH:mm:ss', new Date()), data: t }));

            const meetingItems = todayMeetings.map((m) => ({
              type: 'meeting',
              sortTime: parseMeetingTime(m.Time) || new Date(0),
              data: m,
            }));

            const combined = [...taskItems, ...meetingItems].sort((a, b) => a.sortTime - b.sortTime);

            return combined.map((item, itemIdx) => {
              if (item.type === 'meeting') {
                const m = item.data;
                const mStart = parseMeetingTime(m.Time);
                const mEnd = parseMeetingTime(m.FinishTime);
                const durationMins = mStart && mEnd ? differenceInMinutes(mEnd, mStart) : 0;
                const now = new Date();
                const isActive = mStart && mEnd && isBefore(mStart, now) && isAfter(mEnd, now);
                const isFinished = m.Start === 1 && m.Finished === 1;
                const isLong = durationMins >= 60;
                const isGoogleMeet = m.Location === 'Google Meet';

                let bg = isGoogleMeet
                  ? 'bg-[#e8f0fe] border-[#1a73e8]'
                  : isFinished
                    ? 'bg-gradient-to-br from-purple-600/90 to-violet-500/90 border-white/20'
                    : isActive
                      ? 'bg-gradient-to-br from-violet-600/90 to-fuchsia-500/90 border-white/20'
                      : 'bg-gradient-to-br from-violet-700/90 to-purple-600/90 border-white/20';

                const textColor = isGoogleMeet ? 'text-[#1a73e8]' : 'text-white';

                return (
                  <li key={`meeting-${m.id}-${itemIdx}`} className="flex flex-col space-y-2">
                    <div className={`${bg} p-3 md:p-4 rounded-2xl border shadow-[0_12px_30px_rgba(109,40,217,0.3)] backdrop-blur-xl relative`}>
                      {/* Header row */}
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {isGoogleMeet && (
                            <img src="https://fonts.gstatic.com/s/i/productlogos/meet_2020q4/v6/web-512dp/logo_meet_2020q4_color_1x_web_512dp.png" alt="Google Meet" className="h-6 w-6 shrink-0" />
                          )}
                          <h3 className={`font-bold text-sm md:text-base break-words ${isGoogleMeet ? 'text-[#1a73e8]' : 'text-white'}`}>{m.Name}</h3>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {isLong && (
                            <span className="inline-flex items-center rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-semibold text-white border border-white/30">
                              {durationMins}m
                            </span>
                          )}
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${isGoogleMeet ? 'bg-[#e8f0fe] text-[#1a73e8] border border-[#1a73e8]/30' : 'bg-white/20 text-white border border-white/30'}`}>
                            {isFinished ? 'Completed' : isActive ? 'In Progress' : m.Type || 'Meeting'}
                          </span>
                        </div>
                      </div>

                      {/* Time + location */}
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <div className="flex items-center gap-1.5">
                          <ClockIcon className={`h-4 w-4 shrink-0 ${isGoogleMeet ? 'text-[#1a73e8]' : 'text-white/80'}`} />
                          <span className={`text-xs font-semibold ${isGoogleMeet ? 'text-[#1a73e8]' : 'text-white'}`}>
                            {m.Time}{m.FinishTime ? ` – ${m.FinishTime}` : ''}
                          </span>
                        </div>
                        {m.Location && (
                          <div className="flex items-center gap-1.5">
                            <MapPinIcon className={`h-4 w-4 shrink-0 ${isGoogleMeet ? 'text-[#1a73e8]' : 'text-white/80'}`} />
                            <span className={`text-xs font-semibold ${isGoogleMeet ? 'text-[#1a73e8]' : 'text-white'}`}>{m.Location}</span>
                          </div>
                        )}
                      </div>

                      {/* Attendees */}
                      {m.Attendees && (
                        <div className={`rounded-xl p-2 mb-2 ${isGoogleMeet ? 'bg-white/70 border border-[#1a73e8]/20' : 'bg-white/15 border border-white/20'}`}>
                          <div className="flex items-start gap-1.5">
                            <UserCircleIcon className={`h-4 w-4 shrink-0 mt-0.5 ${isGoogleMeet ? 'text-[#1a73e8]' : 'text-white/70'}`} />
                            <div>
                              <span className={`block text-[10px] font-semibold uppercase tracking-wide ${isGoogleMeet ? 'text-gray-500' : 'text-white/60'}`}>Attendees</span>
                              <p className={`text-xs leading-snug ${isGoogleMeet ? 'text-[#1a73e8]' : 'text-white/90'}`}>{m.Attendees}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      {m.Notes && (
                        <p className={`text-xs leading-relaxed mb-2 ${isGoogleMeet ? 'text-gray-700' : 'text-white/80'}`}>{m.Notes}</p>
                      )}

                      {/* Action button */}
                      {!isFinished && (
                        <div className="mt-2">
                          {isGoogleMeet && m.GoogleMeetURL ? (
                            <button
                              onClick={() => window.open(m.GoogleMeetURL, '_blank')}
                              className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-br from-sky-600/90 to-sky-500/90 border border-white/20 px-3 py-1.5 text-xs font-semibold text-white shadow-lg hover:from-sky-500/95 transition-all"
                            >
                              <img src="https://fonts.gstatic.com/s/i/productlogos/meet_2020q4/v6/web-512dp/logo_meet_2020q4_color_1x_web_512dp.png" alt="Meet" className="h-4 w-4 bg-white rounded-full p-0.5" />
                              Join Google Meet
                            </button>
                          ) : m.Interview === 1 ? (
                            <button
                              onClick={() => Navigate('/interview', { state: { meeting: m } })}
                              className="inline-flex items-center gap-1.5 rounded-xl bg-white/20 border border-white/30 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/30 transition-all"
                            >
                              Start Interview
                            </button>
                          ) : (
                            <button
                              onClick={() => m.Type === 'InsightDocs' ? window.open('https://docs.insightos.co.uk', '_blank') : Navigate('/meeting', { state: { meeting: m } })}
                              className="inline-flex items-center gap-1.5 rounded-xl bg-white/20 border border-white/30 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/30 transition-all"
                            >
                              Start Meeting
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                );
              }

              // ── Task item ───────────────────────────────────────────────
              const task = item.data;
              const taskStartTime = parse(task.StartTime, "HH:mm:ss", new Date());
              const now = new Date();
            const taskEndTime = getTaskEndTime(task, taskStartTime);
            let cardColor = "bg-gradient-to-br from-indigo-900/90 to-indigo-700/90";
            const isBreak = task.Break === 1;
            const completed = task.Completed;

            let icon = <ClockIcon className="h-6 w-6 text-white" aria-hidden="true" />;
            let button = null;
            let issue = null;
            let knowledgeButton = null;

            if (isBefore(taskEndTime, now)) {
              cardColor = "bg-gradient-to-br from-sky-600/90 to-sky-500/90";
              icon = completed === 1 
                ? 
              <CheckCircleIcon className="h-12 w-12 text-emerald-300 animate-bounce" aria-hidden="true" />
                : <ExclamationCircleIcon className="h-6 w-6 text-white animate-pulse" aria-hidden="true" />;

              if (completed !== 1 && task.Task !== "Break") {
                button = (
                  <button 
                    onClick={() => handleCompleteTask(task.TaskID)}
                    data-feature="complete-task"
                    className="bg-gradient-to-br from-emerald-600/90 to-emerald-500/90 border border-white/20 px-3 py-2 text-white rounded-xl hover:from-emerald-500/95 hover:to-emerald-400/95 transition-all shadow-lg shadow-emerald-900/35"
                  >
                    Completed Task
                  </button>
                );

                issue = (
                  <button 
                    onClick={() => handleRaiseIssue(task.TaskID, task.id)

                    }
                    data-feature="raise-issue"
                    className="bg-gradient-to-br from-red-600/90 to-red-500/90 border border-white/20 px-3 py-2 text-white rounded-xl hover:from-sky-500/95 hover:to-sky-400/95 transition-all shadow-lg shadow-sky-900/35"
                  >
                    Raise Issue
                  </button>
                );
              }
            } else if (isEqual(taskStartTime, now) || (isBefore(taskStartTime, now) && isAfter(taskEndTime, now))) {
              cardColor = "bg-gradient-to-br from-emerald-600/90 to-emerald-500/90";
              icon = <ExclamationCircleIcon className="h-6 w-6 text-white" aria-hidden="true" />;
              button = (
                <button 
                  onClick={() => handleRaiseIssue(task.TaskID, task.id)}
                  data-feature="ask-for-help"
                  className="bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border border-white/20 px-3 py-2 text-white rounded-xl hover:from-indigo-800/95 hover:to-indigo-600/95 transition-all shadow-lg shadow-indigo-950/35"
                >
                  Ask For Help?
                </button>
              );

              knowledgeButton = (
                <button onClick={() => Navigate('/knowledge-tree')} className="bg-white/85 border border-indigo-200/70 px-3 py-2 rounded-xl hover:bg-white transition-all" data-feature="open-knowledge-tree">
                  <img src="tree.png" alt="Knowledge Tree" className="w-6 h-6" />
                </button>
              );
            }

            return (
              <li key={task.TaskID} className="flex flex-col space-y-2">
                {/* Break card (top) - only show if it's a break task */}
                {isBreak && task.BreakStart && task.BreakEnd && (
                  <div className="bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 border border-indigo-200/60 p-3 md:p-4 rounded-2xl shadow-[0_12px_30px_rgba(99,102,241,0.2)] text-indigo-900 relative">
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm md:text-base">Break Time</h3>
                        <p className="text-xs md:text-sm">
                          {task.BreakStart} - {task.BreakEnd}
                        </p>
                      </div>
                     
                    </div>
                  </div>
                )}
                
                {/* Main task card */}
                <div className={`${cardColor} p-3 md:p-4 rounded-2xl border border-white/20 shadow-[0_12px_30px_rgba(15,23,42,0.25)] text-white relative backdrop-blur-xl`}>
                  <div className="flex justify-between items-center">
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white text-indigo-600 font-semibold text-sm md:text-base">
                      {task.Step}
                    </div>
                    {task.ProjectName !== null && (
                      <div className="flex items-center space-x-2">
                  <button
                    onClick={() => Navigate('/project-management', { state: { projectId: task.ProjectId } })}
                    type="button"
                    className="inline-flex items-center justify-center px-2 py-1 md:px-3 md:py-2 text-xs md:text-sm bg-white/85 border border-indigo-200/70 text-indigo-900 hover:bg-white hover:animate-bounce focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-xl transition-all"
                  >
                    {task.ProjectName}
                  </button>
                  </div>
                    )}
                    {task.messages.length > 0 && (
                      <button onClick={() => handleOpenMessages(task.TaskID, task.messages)} className="bg-white/85 border border-indigo-200/70 p-2 rounded-full hover:bg-white transition-all" data-feature="open-task-messages">
                        <ChatBubbleBottomCenterIcon className="w-10 h-10 md:w-16 md:h-16 sm:w-6 sm:h-6 hover:text-emerald-500 animate-bounce text-indigo-600" />
                      </button>
                    )}
                    <button onClick={() => handleTaskSelect(task.id)} className="focus:outline-none" data-feature="toggle-task-details">
                      <ChevronDoubleRightIcon className="w-10 h-10 md:w-16 md:h-16 text-white" />
                    </button>
                  </div>
                  <div>{icon}</div>
                  <p className="text-sm mt-2"> {task.ProcessName}</p>
                  <p className="font-semibold">{task.Task}</p>
                  <p className="text-sm mt-2">
                    <time>{format(taskStartTime, "HH:mm")}</time> -{" "}
                    <time>{format(taskEndTime, "HH:mm")}</time>
                  </p>
                  <p className="text-sm mt-2">{task.Description}</p>
                  {task.Completed === 1 && (
                    <p className="text-sm mt-2 p-2 font-bold bg-white/85 border border-indigo-200/70 text-indigo-700 rounded-xl inline-block">
                      Task Outcome: {task.Outcome}
                    </p>
                  )}

                  {task.Completed === 0 && task.issues?.length > 0 && task.Task !== "Break" && (
                    <div className="mt-4 space-y-3">
                {task.issues.map((issue, index) => (
                <div
                key={index}
                className="bg-white/85 backdrop-blur-sm p-4 rounded-xl shadow-sm border border-rose-200/70"
                >
                {/* Issue Header */}
                <div className="flex items-center space-x-2 mb-2">
                <ExclamationCircleIcon className="h-6 w-6 text-red-500" />
                <h3 className="text-red-600 font-semibold text-sm">Issue Reported</h3>
                </div>

                {/* Issue Details */}
                <p className="text-sm text-gray-800">
                <span className="font-medium text-red-600">Description: </span>
                {issue.Description}
                </p>

                <p className="text-sm text-gray-800 mt-1">
                <span className="font-medium text-red-600">Attempted Fix: </span>
                {issue.Tried}
                </p>

                {/* Help Responses */}
                {issue.help?.length > 0 && (
                <div className="mt-3 space-y-3">
                  {issue.help.map((helpItem, helpIndex) => (
                  <div
                  key={helpIndex}
                  className="flex flex-col p-4 bg-emerald-100/80 border border-emerald-200/70 rounded-xl shadow-sm"
                  >
                  {/* Rating — only show if not yet rated */}
                  {!ratings[helpItem.id] && (
                  <div className="flex items-center space-x-2 pb-2">
                    <span className="text-sm font-medium text-gray-700">
                    Rate Response:
                    </span>
                    {[1, 2, 3, 4, 5].map((star) => (
                    <button
                    key={star}
                    className="h-5 w-5 text-lg text-gray-400 hover:text-yellow-500 transition-colors duration-200"
                    onClick={() => handleRating(helpItem.id, star)}
                    >
                    ★
                    </button>
                    ))}
                  </div>
                  )}
                  

                  {/* Help Response */}
                  <div className="text-sm p-2 bg-gradient-to-br from-emerald-600/90 to-emerald-500/90 text-white rounded-xl border border-white/20">
                    {helpItem.Description.split('###').map((para, index) => (
                      <p key={index}>
                        {para.split('**').map((line, i) => (
                          <React.Fragment key={i}>
                            {line}
                            {i < para.split('**').length - 1 && <br />}
                          </React.Fragment>
                        ))}
                      </p>
                    ))}
                  </div>
                   
                  {/* Staff Name & Created At */}
                  <div className="flex justify-between items-center mt-2 text-xs text-gray-600">
                  <span>By: {helpItem.StaffName}</span>
                  <span>{new Date(helpItem.CreatedAt).toLocaleString()}</span>
                  </div>
                  </div>
                  ))}
                </div>
                )}
                </div>
                ))}
              </div>
              )}

              {task.TaskID === openOutcome && (
              <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50 p-4">
                <div className="bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl p-4 rounded-2xl border border-indigo-200/60 shadow-[0_12px_30px_rgba(99,102,241,0.2)] w-full max-w-md">
                <h2 className="text-lg font-semibold text-gray-800">
                Outcome for Task
                </h2>
                <textarea
                value={instructionText}
                onChange={(e) => setInstructionText(e.target.value)}
                data-component="task-outcome-input"
                className="w-full h-32 p-2 border border-indigo-200/70 bg-white/85 rounded-xl mt-2 text-black"
                placeholder="Enter the outcome for this task..."
                />
                <div className="flex items-center justify-end mt-4 space-x-2">
                <button
                onClick={() => setOpenOutcome(null)}
                data-feature="cancel-task-outcome"
                className="bg-white/80 border border-indigo-200/70 text-indigo-900 px-3 py-2 rounded-xl shadow-sm hover:bg-white text-sm md:text-base transition-all"
                >
                Cancel
                </button>
                <button
                onClick={() => handleSaveOutcome(openOutcome)}
                data-feature="save-task-outcome_post"
                className="bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border border-white/20 text-white px-3 py-2 rounded-xl shadow-lg shadow-indigo-950/35 hover:from-indigo-800/95 hover:to-indigo-600/95 text-sm md:text-base transition-all"
                >
                Save
                </button>
                </div>
                </div>
              </div>
              )}
              

                  {task.Task !== "Break" && (
                    <div className="flex flex-wrap gap-1 md:gap-2 mt-3 md:mt-4">
                      {button}
                      {knowledgeButton}
                      {issue}
                      <button 
                        onClick={() => Navigate('/course', {state: {courseDocName: task.ProcessName}})} 
                        data-feature="view-task-documents"
                        className="bg-white/80 border border-indigo-200/70 px-2 py-1 md:px-3 md:py-2 text-xs md:text-sm text-indigo-900 rounded-xl hover:bg-white transition-all"
                      >
                        View Documents
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          });
        })()}
        </ul>
      </div>

      {/* Task Details Panel */}
      {selectedTaskId && !messagesTaskId && (
        <div className={`w-full md:w-1/3 bg-gradient-to-br from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl ${window.innerWidth < 768 ? 'fixed inset-0 z-50 flex flex-col' : 'border-l border-indigo-200/60'} shadow-[0_12px_30px_rgba(99,102,241,0.2)] transition-all duration-300`}>
          {/* ...existing code for substep viewer panel... */}
          <div className="md:hidden bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 text-white p-4 flex items-center justify-between shadow-lg shadow-indigo-950/35">
            <h2 className="text-lg font-semibold">Task Details</h2>
            <button
              onClick={() => {
                setSelectedTaskId(null);
              }}
              data-feature="close-task-details"
              className="p-2 hover:bg-indigo-700 rounded-full transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          <button
            onClick={() => {
              setSelectedTaskId(null);
            }}
            data-feature="close-task-details"
            className="hidden md:block mb-4 mt-4 mx-4 px-3 py-1 bg-gradient-to-br from-sky-600/90 to-sky-500/90 text-white border border-white/20 rounded-xl hover:from-sky-500/95 hover:to-sky-400/95 transition-all flex items-center w-fit shadow-lg shadow-sky-900/35"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            Close
          </button>
          <div className="flex-1 overflow-auto p-4">
            <SubstepViewer taskData={selectedTaskId} />
          </div>
          <div className="md:hidden bg-gray-100 p-4 border-t">
            <button
              onClick={() => {
                setSelectedTaskId(null);
              }}
              data-feature="close-task-details"
              className="w-full py-3 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 text-white border border-white/20 rounded-xl hover:from-indigo-800/95 hover:to-indigo-600/95 transition-all font-medium shadow-lg shadow-indigo-950/35"
            >
              Close Task Details
            </button>
          </div>
        </div>
      )}

      {/* Full screen overlay for messages */}
      {messagesTaskId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-indigo-400/40 via-purple-300/30 to-pink-300/40 backdrop-blur-xl">
          <div className="w-full max-w-2xl mx-auto rounded-2xl shadow-2xl bg-white/30 border border-white/20 p-0 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-indigo-600/80 rounded-t-2xl">
              <h2 className="text-xl font-bold text-white drop-shadow">Task Messages</h2>
              <button
                onClick={() => {
                  setMessagesTaskId(null);
                  setCurrentTaskMessages([]);
                }}
                data-feature="close-task-messages"
                className="p-2 hover:bg-indigo-700 rounded-full transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            {/* Messages List and Input */}
            <div className="flex-1 overflow-auto p-6 space-y-3">
              {/* Messages List */}
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {currentTaskMessages.length === 0 ? (
                  <p className="text-white/80 text-center py-4 text-base font-semibold rounded-xl bg-gradient-to-r from-indigo-400/20 to-pink-300/20 shadow">No messages yet</p>
                ) : (
                  currentTaskMessages.map((message, index) => (
                    <div key={message.id || index} className="rounded-lg p-2 bg-white/30 backdrop-blur-md shadow border border-white/20 flex flex-col gap-1">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-indigo-700 bg-white/60 px-2 py-0.5 rounded shadow-sm text-xs">{message.from}</span>
                          {message.to && (
                            <span className="text-xs text-purple-700 bg-white/40 px-2 py-0.5 rounded">To: {message.to}</span>
                          )}
                        </div>
                        <div className="flex flex-col items-end min-w-[80px]">
                          <span className="text-[10px] text-gray-700 font-semibold bg-white/40 px-1 py-0.5 rounded mb-0.5">{new Date(message.createdAt).toLocaleDateString()}</span>
                          <span className="text-[10px] text-gray-700 font-semibold bg-white/40 px-1 py-0.5 rounded">{new Date(message.createdAt).toLocaleTimeString()}</span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-900 font-normal mb-0.5 break-words">{message.content}</p>
                    </div>
                  ))
                )}
              </div>
              {/* Message Input */}
              <div className="border-t border-white/20 pt-3 mt-3">
                <div className="flex space-x-2 items-end">
                  <textarea
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    placeholder="Type your message..."
                    data-component="task-message-input"
                    className="flex-1 resize-none rounded-xl px-3 py-2 text-sm text-indigo-900 bg-white/20 border border-white/20 shadow focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 placeholder:text-indigo-400/60 backdrop-blur-md"
                    rows={2}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim()}
                    data-feature="send-task-message_post"
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white font-bold shadow hover:scale-105 transition-transform focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    
    </div>

 

      
  
  
  );
}
